import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, ZoomIn, ZoomOut, Download, X } from 'lucide-react';
import SystemHeader from '../SystemHeader';
import { markdownToHtml } from '../../utils/markdownToHtml';

// ─── File type detection ────────────────
const getFileType = (file) => {
  if (!file) return 'unknown';
  const ext = file.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'md') return 'markdown';
  return 'unknown';
};

// ─── Image Modal ────────────────────────
const ImageModal = ({ src, onClose }) => {
  const [zoomed, setZoomed] = useState(false);
  if (!src) return null;
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2 animate-fade-in" onClick={onClose}>
      <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors z-10">
        <X size={18} className="text-white" />
      </button>
      <div
        className={`max-w-full max-h-full overflow-auto ${zoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
        onClick={(e) => { e.stopPropagation(); setZoomed(!zoomed); }}
      >
        <img
          src={src}
          alt=""
          className="transition-all duration-200"
          style={zoomed ? { maxWidth: 'none', width: 'auto' } : { maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
        />
      </div>
    </div>
  );
};

// ─── Markdown Viewer ────────────────────
const MarkdownViewer = ({ filePath }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalImage, setModalImage] = useState(null);
  const articleRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/kienthuc/${filePath}?v=${Date.now()}`);
        if (!res.ok) throw new Error('Không tìm thấy file');
        setContent(await res.text());
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    };
    load();
  }, [filePath]);

  // Attach click handlers to all images after render
  useEffect(() => {
    if (!articleRef.current) return;
    const images = articleRef.current.querySelectorAll('img');
    const handlers = [];
    images.forEach((img) => {
      img.style.cursor = 'zoom-in';
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setModalImage(img.src);
      };
      img.addEventListener('click', handler);
      handlers.push({ img, handler });
    });
    return () => {
      handlers.forEach(({ img, handler }) => img.removeEventListener('click', handler));
    };
  }, [content]);

  if (loading) return <div className="text-center py-12 text-gray-500 text-sm">Đang tải...</div>;
  if (error) return <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{error}</div>;

  const basePath = filePath ? filePath.split('/').slice(0, -1).join('/') : '';

  return (
    <>
      <article
        ref={articleRef}
        className="px-4 py-4 text-[13px] text-gray-700 leading-normal markdown-content"
        dangerouslySetInnerHTML={{ __html: markdownToHtml(content, basePath) }}
      />
      {modalImage && <ImageModal src={modalImage} onClose={() => setModalImage(null)} />}
    </>
  );
};

// ─── Image Viewer ───────────────────────
const ImageViewer = ({ filePath, label }) => {
  const [zoomed, setZoomed] = useState(false);

  const imgUrl = `/kienthuc/${filePath}`;

  return (
    <div className="p-4 flex flex-col gap-3">
      {/* Controls */}
      <div className="flex items-center gap-2 justify-end">
        <button
          onClick={() => setZoomed(!zoomed)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-medium hover:bg-gray-200 transition-colors"
        >
          {zoomed ? <ZoomOut size={14} /> : <ZoomIn size={14} />}
          {zoomed ? 'Thu nhỏ' : 'Phóng to'}
        </button>
        <a
          href={imgUrl}
          download
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 text-xs font-medium hover:bg-blue-100 transition-colors"
        >
          <Download size={14} />
          Tải về
        </a>
      </div>

      {/* Image */}
      <div
        className={`rounded-xl border border-gray-200 overflow-hidden bg-gray-50 ${zoomed ? 'overflow-auto' : ''}`}
        onClick={() => setZoomed(!zoomed)}
      >
        <img
          src={imgUrl}
          alt={label || 'Ảnh'}
          className={`${zoomed ? 'w-auto max-w-none cursor-zoom-out' : 'w-full h-auto cursor-zoom-in'} transition-all`}
          onError={(e) => { e.target.alt = 'Không tải được ảnh'; }}
        />
      </div>
    </div>
  );
};

// ─── PDF Viewer ─────────────────────────
const PdfViewer = ({ filePath, label }) => {
  const pdfUrl = `/kienthuc/${filePath}`;

  return (
    <div className="p-4 flex flex-col gap-3">
      {/* Controls */}
      <div className="flex items-center gap-2 justify-end">
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-medium hover:bg-gray-200 transition-colors"
        >
          Mở tab mới ↗
        </a>
        <a
          href={pdfUrl}
          download
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 text-xs font-medium hover:bg-blue-100 transition-colors"
        >
          <Download size={14} />
          Tải về
        </a>
      </div>

      {/* PDF embed */}
      <div className="rounded-xl border border-gray-200 overflow-hidden bg-gray-100" style={{ minHeight: '70vh' }}>
        <iframe
          src={pdfUrl}
          title={label || 'PDF'}
          className="w-full h-full border-none"
          style={{ minHeight: '70vh' }}
        />
      </div>
    </div>
  );
};

// ─── Main Detail Component ──────────────
const KienThucDetail = ({ userInfo, onLogout, totalPoints, onRefreshPoints }) => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const location = useLocation();

  // Get item info from navigation state or fallback
  const itemFromState = location.state?.item;
  const [item, setItem] = useState(itemFromState);

  // If no state, try to find item in config
  useEffect(() => {
    if (!itemFromState) {
      const findItem = async () => {
        try {
          const res = await fetch(`/kienthuc/kienthuc.config.json?v=${Date.now()}`);
          if (res.ok) {
            const data = await res.json();
            const found = findInTree(data.tree || [], slug);
            if (found) setItem(found);
            else {
              // Fallback: assume it's a .md file with slug as filename
              setItem({ id: slug, label: slug, type: 'file', file: slug + '.md', enabled: true });
            }
          }
        } catch { 
          setItem({ id: slug, label: slug, type: 'file', file: slug + '.md', enabled: true });
        }
      };
      findItem();
    }
  }, [slug, itemFromState]);

  if (!item) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500 text-sm">Đang tải...</div>;

  const fileType = getFileType(item.file);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SystemHeader userInfo={userInfo} onLogout={onLogout} totalPoints={totalPoints} pageTitle="Kiến thức" onRefreshPoints={onRefreshPoints} />

      <div className="flex-1 max-w-2xl w-full mx-auto flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200">
          <button onClick={() => navigate('/kien-thuc')} className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold text-gray-900 truncate">{item.label || slug}</h1>
            {fileType !== 'markdown' && (
              <p className="text-[10px] text-gray-400 mt-0.5">
                {fileType === 'image' ? '🖼️ Ảnh' : fileType === 'pdf' ? '📑 PDF' : '📄 File'}
              </p>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-white">
          {fileType === 'markdown' && <MarkdownViewer filePath={item.file} />}
          {fileType === 'image' && <ImageViewer filePath={item.file} label={item.label} />}
          {fileType === 'pdf' && <PdfViewer filePath={item.file} label={item.label} />}
          {fileType === 'unknown' && (
            <div className="text-center py-12 text-gray-400 text-sm">
              Không hỗ trợ xem file này.
              <br />
              <a href={`/kienthuc/${item.file}`} download className="text-blue-600 underline mt-2 inline-block">Tải về</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper: find item by id in tree (recursive)
function findInTree(tree, id) {
  for (const item of tree) {
    if (item.id === id) return item;
    if (item.children) {
      const found = findInTree(item.children, id);
      if (found) return found;
    }
  }
  return null;
}

export default KienThucDetail;
